import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { Points } from 'components/Points/Points';
import MagicButton from 'components/MagicButton/MagicButton';
import LoginPromptModal from 'components/LoginPromptModal/LoginPromptModal';
import { AssetUrls } from 'config/assets';
import { SetIcon } from 'components/SetIcon';
import { buildCardUrl } from 'utils/cardUrlUtils';
import './HoverOverlay.css';

interface HoverOverlayProps {
  title: string;
  cardId: string | number;
  oracleId?: string;
  setName?: string;
  setCode?: string;
  setIconUrl?: string;
  collectorNumber?: string;
  nonFoilPrice?: number;
  foilPrice?: number;
  availableInNonFoil?: boolean;
  availableInFoil?: boolean;
  onClose?: () => void;
  isMobile?: boolean;
  onNavigate?: () => void;
}

const HoverOverlay = ({
  title,
  cardId,
  oracleId,
  setName,
  setCode,
  setIconUrl,
  collectorNumber,
  nonFoilPrice,
  foilPrice,
  availableInNonFoil,
  availableInFoil,
  onClose,
  isMobile = false,
  onNavigate,
}: HoverOverlayProps) => {
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const handleCardNavigation = () => {
    onNavigate?.();
    navigate(buildCardUrl({ name: title, setCode, collectorNumber, scryfallID: String(cardId) }), {
      state: {
        from: 'search',
        returnPath: window.location.pathname + window.location.search
      }
    });
  };

  const handleWantClick = () => {
    // If not logged in, show login modal
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }

    onNavigate?.();
    // Navigate to view-card-printings page with the card name and ID for exact matching
    const encodedName = encodeURIComponent(title);
    navigate(`/view-card-printings/${encodedName}?cardId=${cardId}`, {
      state: {
        from: 'search',
        returnPath: window.location.pathname + window.location.search
      }
    });
  };

  // Get the best available price
  const getCurrentPrice = () => {
    // Prefer non-foil price, but show foil if that's all we have
    if (nonFoilPrice !== undefined && nonFoilPrice !== null && nonFoilPrice !== 0) {
      return nonFoilPrice;
    }
    
    if (foilPrice !== undefined && foilPrice !== null && foilPrice !== 0) {
      return foilPrice;
    }
    
    return null;
  };

  return (
    <div className="hover-overlay">
      {/* Mobile close button */}
      {isMobile && onClose && (
        <button className="mobile-close-btn" onClick={onClose} aria-label="Close">
          ×
        </button>
      )}
      
      {/* Card title with inline arrow icon */}
      <div 
        className="hover-title-row"
        onClick={handleCardNavigation}
      >
        <span className="hover-title">{title}</span>
        <img 
          className="hover-arrow-inline"
          src={`${AssetUrls.base}/assets/icons/Search/icon-arrow-diagonal.svg`}
          alt="View details"
        />
      </div>

      {/* Set icon and name */}
      {setName && (
        <div className="hover-set-info">
          <SetIcon setIconUrl={setIconUrl} setName={setName} size="lg" />
          <span className="hover-set-name">{setName}</span>
          {collectorNumber && (
            <span className="hover-collector-number"> #{collectorNumber}</span>
          )}
        </div>
      )}

      {/* Price section */}
      <div className="hover-price-section">
        {getCurrentPrice() !== null && getCurrentPrice() !== 0 ? (
          <Points type="big" points={getCurrentPrice()!} />
        ) : (
          <div className="price-unavailable">???</div>
        )}
      </div>

      {/* Want button */}
      <div className="hover-action-row">
        <MagicButton
          label="Want"
          icon="heart"
          color="black"
          onPress={handleWantClick}
          fullWidth
        />
      </div>

      {/* Login prompt modal for unauthenticated users */}
      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        collectionOwnerUsername={title}
      />
    </div>
  );
};

export default HoverOverlay;
